import { Component, ElementRef, HostListener, Inject, OnInit, ViewChild } from '@angular/core';
import { Home } from 'lucide-angular';
import { NgxSpinnerService } from 'ngx-spinner';
import { NavigationEnd, Router } from '@angular/router';
import { OKTA_AUTH, OktaAuthStateService } from '@okta/okta-angular';
import { AuthState, OktaAuth } from '@okta/okta-auth-js';
import { filter, Observable } from 'rxjs';
import { AuthService } from './Modules/auth/auth.service';
import { LaunchDarklyService } from './core/services/launch-darkly.service';
import { RootScopeService } from './core/services/root-scope.service';
import { ClientDashboardService } from './Modules/client-dashboard/client-dashboard.service';
import { ReferralService } from './Modules/referral/referral.service';
 
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit {
  title = 'FinanceDeNavigator';
  public isAuthenticated$!: Observable<boolean>;
  isLoading: boolean = false;
  isInCallback: boolean = false;
  protected readonly Home = Home;
  isReferralsPage = false;
  referralsLastRefreshed = '';

  protected profileDropdownOpen = false;
  @ViewChild('profileWrapper', { static: false }) profileWrapperRef?: ElementRef<HTMLElement>;

  // Export button state
  isExporting = false;
  isClientDashboard = false;
  clientId = '';
  get userName(): string {
        return this.rootScopeService.globals.currentUserName ? this.rootScopeService.globals.currentUserName : '';
    }
 
 
  private labels: { [key: string]: string } = {
    '/home': 'Home',
    '/fileGrid': 'Invoice Extract',
    '/CBEM': 'CBEM Dashboard',
    '/ClientNav': 'Master Client Database',
    '/ClientNavigator/:name': 'Master Client Database',
    '/Referrals': 'Referrals',
    '/DynamoUtility': 'DynamoDB Utility',
  };
 
  constructor(
    private router: Router,
    private spinner: NgxSpinnerService,
    private _oktaStateService: OktaAuthStateService,
    @Inject(OKTA_AUTH) private _oktaAuth: OktaAuth,
    private authService: AuthService,
    private ldService: LaunchDarklyService,
    private rootScopeService: RootScopeService,
    private clientDashboardService: ClientDashboardService,
    private referralService: ReferralService
  ) {
    // Track route changes to detect client dashboard page and extract clientId
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((event) => {
        this.updateClientDashboardState(event.urlAfterRedirects || event.url);
        this.updateReferralsHeaderState(event.urlAfterRedirects || event.url);
      });
    this.updateReferralsHeaderState(this.router.url);
    this.referralService.referralRefreshDate$.subscribe((value) => {
      if (value) {
        this.referralsLastRefreshed = value;
      }
    });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (!this.profileDropdownOpen) return;
    const wrapper = this.profileWrapperRef?.nativeElement;
    const target = event.target as Node | null;
    if (wrapper && target && !wrapper.contains(target)) {
      this.profileDropdownOpen = false;
    }
  }
 
async ngOnInit() {
    this.spinner.show();
  this.checkIfCallbackRoute();
 
  this.router.events.subscribe(() => {
    this.checkIfCallbackRoute();
  });
 
  // Wait for Okta authentication and set credentials first
  await this.waitForAuthenticationAndSetCredentials();
 
  // Now initialize LaunchDarkly with proper user context
  await this.ldService.initialize();
 
  // Fetch initial flags
  const viewContacts = this.ldService.getFlag('view-contacts-flag', false);
  this.rootScopeService.setViewContactsFlag(viewContacts);
  this.rootScopeService.setMasterClientVisible(this.ldService.getFlag('master-client-visible', false));
  this.rootScopeService.setCbemVisible(this.ldService.getFlag('CBEM-visible', false));
  this.rootScopeService.setInvoiceExtractVisible(this.ldService.getFlag('invoice-extract-visible', false));
  this.rootScopeService.setReferralVisible(this.ldService.getFlag('gb-referrals-visible', false));
  this.rootScopeService.setAddGbReferralsVisible(this.ldService.getFlag('add-gb-referrals', false));
  this.rootScopeService.setEditGbReferralsVisible(this.ldService.getFlag('edit-gb-referrals', false));
  this.rootScopeService.setDeleteGbReferralsVisible(this.ldService.getFlag('delete-gb-referrals', false));
  this.rootScopeService.setDynamoUtilityVisible(this.ldService.getFlag('utility-visible', false));
  console.log('LD flags initial:', {
    viewContacts,
    masterClientVisible: this.rootScopeService.masterClientVisible(),
    cbemVisible: this.rootScopeService.cbemVisible(),
    invoiceExtractVisible: this.rootScopeService.invoiceExtractVisible(),
    referralVisible: this.rootScopeService.referralVisible(),
    addGBReferral: this.rootScopeService.addGbReferralsVisible(),
    editGBReferral: this.rootScopeService.editGbReferralsVisible(),
    deleteGBReferral: this.rootScopeService.deleteGbReferralsVisible(),
    dynamoUtilityVisible: this.rootScopeService.dynamoUtilityVisible(),
  });
 
  // Mark flags ready after initial load
  this.rootScopeService.setLaunchDarklyReady(true);

  // Subscribe to changes
  this.ldService.onFlagChange('view-contacts-flag', (n: boolean) => this.rootScopeService.setViewContactsFlag(n));
  this.ldService.onFlagChange('master-client-visible', (n: boolean) => this.rootScopeService.setMasterClientVisible(n));
  this.ldService.onFlagChange('CBEM-visible', (n: boolean) => this.rootScopeService.setCbemVisible(n));
  this.ldService.onFlagChange('invoice-extract-visible', (n: boolean) => this.rootScopeService.setInvoiceExtractVisible(n));
  this.ldService.onFlagChange('gb-referrals-visible', (n: boolean) => this.rootScopeService.setReferralVisible(n));
  this.ldService.onFlagChange('add-gb-referrals', (n: boolean) => this.rootScopeService.setAddGbReferralsVisible(n));
  this.ldService.onFlagChange('edit-gb-referrals', (n: boolean) => this.rootScopeService.setEditGbReferralsVisible(n));
  this.ldService.onFlagChange('delete-gb-referrals', (n: boolean) => this.rootScopeService.setDeleteGbReferralsVisible(n));
  this.ldService.onFlagChange('utility-visible', (n: boolean) => this.rootScopeService.setDynamoUtilityVisible(n));
 
  this.isLoading = false;
  this.spinner.hide();
}
 
private async waitForAuthenticationAndSetCredentials(): Promise<void> {
  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      console.warn('Authentication timeout - proceeding with anonymous context');
      this.isLoading = false;
      resolve();
    }, 10000); // 10 second timeout
 
    this._oktaStateService.authState$
      .pipe(filter((s: AuthState) => Boolean(s && s.isAuthenticated)))
      .subscribe(async () => {
        try {
          clearTimeout(timeout);
          await this.authService.setCredentials();
          console.log(' User credentials set successfully');
          resolve();
        } catch (error) {
          console.error(' Failed to set credentials:', error);
          reject(error);
        }
      });
  });
}
 
public showSpinner(){
  setTimeout(() => {
      this.spinner.show(undefined, {
      type: 'ball-clip-rotate-multiple',
      size: 'large',
      bdColor: 'rgba(0, 0, 0, 0.9)',
      color: '#fff',
      fullScreen: true
    });
  },5000)
}
 
private checkIfCallbackRoute() {
  this.isInCallback = this.router.url.startsWith('/login/callback');
}
 
  public async signIn() {
    await this.authService.loginWithOkta();
  }
 
  public async signOut() {
    await this.authService.logoutWithOkta();
  }

  protected toggleProfileDropdown(event: MouseEvent) {
    event.stopPropagation();
    this.profileDropdownOpen = !this.profileDropdownOpen;
  }

  protected async onSignOutClick(event: MouseEvent) {
    event.stopPropagation();
    this.profileDropdownOpen = false;
    await this.signOut();
  }
 
  get isHome(): boolean {
    return this.router.url === '/home' || this.router.url === '/';
  }

  private updateClientDashboardState(url: string): void {
    const path = url.split('?')[0];
    this.isClientDashboard = /^\/ClientNavigator\/[^\/]+$/.test(path);

    if (this.isClientDashboard) {
      // Extract clientId from query params
      const queryString = url.split('?')[1] || '';
      const params = new URLSearchParams(queryString);
      this.clientId = params.get('id') || '';
    } else {
      this.clientId = '';
      this.isExporting = false;
    }
  }

  private updateReferralsHeaderState(url: string): void {
    const path = url.split('?')[0];
    const isReferrals = path === '/Referrals';

    if (isReferrals && !this.isReferralsPage) {
      this.referralsLastRefreshed = 'Loading...';
    }

    this.isReferralsPage = isReferrals;
  }


  get exportButtonVisible(): boolean {
    return this.referralService.exportButtonState().visible;
  }

  get exportButtonDisabled(): boolean {
    return this.referralService.exportButtonState().disabled;
  }

  get isExportingReferrals(): boolean {
    return this.referralService.exportButtonState().isExporting;
  }

  get exportButtonTooltip(): string {
    return this.referralService.exportButtonState().tooltip;
  }

  onExportReferralsClick(): void {
    this.referralService.triggerExport();
  }

  exportClientData(): void {
    if (this.isExporting || !this.clientId) return;
    this.isExporting = true;

    this.clientDashboardService.getPresignedUrl(this.clientId).subscribe({
      next: (response) => {
        if (response && response.downloadURL) {
          window.open(response.downloadURL, '_blank');
        }
        this.isExporting = false;
      },
      error: (err) => {
        console.error('Failed to get client export URL', err);
        this.isExporting = false;
      },
      complete: () => {
        this.isExporting = false;
      },
    });
  }
 
  get breadcrumbItems(): Array<{label: string, route?: string}> {
    const currentUrl = this.router.url.split('?')[0];
   
    // Check if we are on the ClientNavigator route with a dynamic name
    const clientNavMatch = currentUrl.match(/^\/ClientNavigator\/([^\/]+)$/);
    if (clientNavMatch && clientNavMatch[1]) {
      // Decode the URL parameter and replace hyphens with spaces for display
      const decodedClientName = decodeURIComponent(clientNavMatch[1]).replace(/-/g, ' ');
      return [
        { label: 'Master Client Database', route: '/ClientNav' },
        { label: decodedClientName }
      ];
    }

    // Check if we are on the ReferralDetail route
    const referralDetailMatch = currentUrl.match(/^\/ReferralDetail\/([^\/]+)$/);
    if (referralDetailMatch && referralDetailMatch[1]) {
      const refNumber = decodeURIComponent(referralDetailMatch[1]);
      return [
        { label: 'Referrals', route: '/Referrals' },
        { label: refNumber }
      ];
    }
   
    // For other pages, return single item
    const label = this.labels[currentUrl] || 'Unknown Page';
    return [{ label }];
  }
}
 